import { isValid, isWeekend, parse } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  type StyleProp,
  StyleSheet,
  Text,
  type TextStyle,
  View,
} from "react-native";
import { z } from "zod";

import {
  FormDatePicker,
  FormProvider,
  FormSelect,
  FormTextInput,
  ResetButton,
  SubmitButton,
  useFormWrapper,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import {
  SCHOOL_LUNCH_BOX_ORDER_DELIVERY_TIMES,
  type SchoolLunchBox,
  type SchoolLunchBoxOrder,
  type SchoolLunchBoxOrderDeliveryTime,
  type SchoolLunchBoxOrderInput,
  type SchoolStudentGroup,
  type SchoolStudentGroupInput,
  UNIQUE_ENTITY_ERROR,
} from "@luna-restaurant/school-api";
import {
  Button,
  NoticeBox,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { getLunchBoxDeliveryTimeTranslationKey } from "@/components/forms/lunchBoxDeliveryTime";
import {
  DATE_ONLY_FORMAT,
  MIN_BUSINESS_DAYS_FOR_LUNA_BOX,
  getMinBusinessDaysForLunchBox,
  getMinLunchBoxDeliveryDate,
  hasEnoughBusinessDaysBeforeLunchBoxDelivery,
} from "@/components/forms/lunchBoxOrderLeadTime";
import { useCollection } from "@/hooks/useCollection";
import { isApiError, useRequest } from "@/hooks/useRequest";

export type NewLunchBoxesFormValues = {
  editor: string;
  date: string;
  time: SchoolLunchBoxOrderDeliveryTime;
  classNameOrGroup: string;
  customGroupName?: string;
  deliveryAddress: string;
  amount: string;
  peopleWithAllergies?: string[];
  lunchBox: string;
  comment?: string;
};

type NewLunchBoxesFormProps = {
  onSubmitSuccess?: (values: NewLunchBoxesFormValues) => void;
  onSubmitInvalid?: () => void;
  onSubmitError?: (error: unknown) => void;
  schoolClassOptions?: { label: string; value: string }[];
  cafeteriaOptions?: { label: string; value: string }[];
  lunchBoxes?: SchoolLunchBox[];
  schoolPath?: string | null;
};

const ALL_CLASSES_OPTION_VALUE = "__ALL__";
const CUSTOM_GROUP_OPTION_VALUE = "__CUSTOM_GROUP__";
const GROUP_OPTION_VALUE_PREFIX = "__GROUP__::";
const MIN_PEOPLE_WITH_ALLERGIES_FIELDS = 1;
const MAX_PERSON_WITH_ALLERGIES_NAME_LENGTH = 255;

type ConstraintViolation = {
  code?: string;
  propertyPath?: string;
  message?: string;
};

const hasDuplicateStudentGroupNameViolation = (error: unknown) => {
  if (!isApiError(error) || error.status !== 422) {
    return false;
  }

  if (
    typeof error.responseBody !== "object" ||
    error.responseBody === null ||
    Array.isArray(error.responseBody)
  ) {
    return false;
  }

  const violations = (error.responseBody as { violations?: unknown })
    .violations;
  if (!Array.isArray(violations)) {
    return false;
  }

  return violations.some((violation) => {
    if (typeof violation !== "object" || violation === null) {
      return false;
    }

    const { propertyPath, code } = violation as ConstraintViolation;
    if (propertyPath !== "name") {
      return false;
    }

    return code === UNIQUE_ENTITY_ERROR;
  });
};

const withImageTransformParams = (imageUrl: string) => {
  const appendParams = (urlValue: URL) => {
    urlValue.searchParams.set("width", "80");
    urlValue.searchParams.set("height", "80");
    urlValue.searchParams.set("fit", "cover");
    return urlValue.toString();
  };

  try {
    return appendParams(new URL(imageUrl));
  } catch {
    return imageUrl;
  }
};

const getLunchBoxImageCandidates = (imageUrl?: string | null) => {
  if (!imageUrl) {
    return [];
  }

  const trimmedImageUrl = imageUrl.trim();
  if (!trimmedImageUrl) {
    return [];
  }

  return Array.from(
    new Set([withImageTransformParams(trimmedImageUrl), trimmedImageUrl]),
  );
};

const getLunchBoxPrimaryImageCandidate = (imageUrl?: string | null) => {
  const [primaryImageCandidate] = getLunchBoxImageCandidates(imageUrl);
  return primaryImageCandidate ?? null;
};

const buildLunchBoxImagePrefetchPayload = (lunchBoxes: SchoolLunchBox[]) => {
  const primaryImageUrls = new Set<string>();

  for (const lunchBox of lunchBoxes) {
    const primaryImageUrl = getLunchBoxPrimaryImageCandidate(lunchBox.image);
    if (!primaryImageUrl) {
      continue;
    }

    primaryImageUrls.add(primaryImageUrl);
  }

  return JSON.stringify(Array.from(primaryImageUrls).sort());
};

type LunchBoxOptionLabelProps = {
  lunchBox: SchoolLunchBox;
  titleStyle: StyleProp<TextStyle>;
  descriptionStyle: StyleProp<TextStyle>;
};

const LunchBoxOptionLabel = ({
  lunchBox,
  titleStyle,
  descriptionStyle,
}: LunchBoxOptionLabelProps) => {
  const [imageCandidateIndex, setImageCandidateIndex] = useState(0);
  const imageCandidates = useMemo(
    () => getLunchBoxImageCandidates(lunchBox.image),
    [lunchBox.image],
  );

  useEffect(() => {
    setImageCandidateIndex(0);
  }, [lunchBox.image]);

  const imageUri = imageCandidates[imageCandidateIndex] ?? null;

  return (
    <View style={styles.lunchBoxOption}>
      {imageUri ? (
        <Image
          source={{ uri: imageUri, cache: "force-cache" }}
          style={styles.lunchBoxOptionImage}
          resizeMode="cover"
          onError={() => {
            setImageCandidateIndex((currentIndex) => currentIndex + 1);
          }}
        />
      ) : null}
      <View style={styles.lunchBoxOptionTextContainer}>
        <ThemedText style={titleStyle}>{lunchBox.title}</ThemedText>
        <ThemedText
          style={[descriptionStyle, styles.lunchBoxOptionDescription]}
        >
          {lunchBox.description ?? ""}
        </ThemedText>
      </View>
    </View>
  );
};

const getCurrentOrderTime = () => new Date();

const sanitizePeopleWithAllergies = (names?: string[]) =>
  (names ?? []).map((name) => name.trim()).filter(Boolean);

const ensurePeopleWithAllergiesInputs = (peopleWithAllergies?: string[]) => {
  if (!Array.isArray(peopleWithAllergies) || peopleWithAllergies.length === 0) {
    return [""];
  }

  return peopleWithAllergies;
};

export const NewLunchBoxesForm = ({
  onSubmitSuccess,
  onSubmitInvalid,
  onSubmitError,
  schoolClassOptions = [],
  cafeteriaOptions = [],
  lunchBoxes = [],
  schoolPath,
}: NewLunchBoxesFormProps = {}) => {
  const { t } = useTranslation();
  const { colors, fonts } = useCustomTheme();
  const orderTimeForView = getCurrentOrderTime();
  const request = useRequest();
  const [isRequestInFlight, setIsRequestInFlight] = useState(false);
  const [isStudentGroupRequestInFlight, setIsStudentGroupRequestInFlight] =
    useState(false);
  const submitInFlightRef = useRef(false);
  const processedPrefetchImageUrlsRef = useRef<Set<string>>(new Set());
  const schoolStudentGroupsQuery = useCollection<SchoolStudentGroup>({
    queryKey: ["school-student-groups", schoolPath],
    path: schoolPath ? `${schoolPath}/student-groups` : "",
    enabled: !!schoolPath,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });
  const studentGroupOptions = useMemo(
    () =>
      schoolStudentGroupsQuery.items.map((studentGroup) => ({
        label: studentGroup.name,
        value: `${GROUP_OPTION_VALUE_PREFIX}${studentGroup["@id"]}`,
      })),
    [schoolStudentGroupsQuery.items],
  );
  const classNameOrGroupOptions = useMemo(
    () => [
      {
        label: t("lunchBoxes.form.classNameAllOption"),
        value: ALL_CLASSES_OPTION_VALUE,
      },
      ...schoolClassOptions,
      ...studentGroupOptions,
      {
        label: t("lunchBoxes.form.classNameOrGroupCustomOption"),
        value: CUSTOM_GROUP_OPTION_VALUE,
        keepOpenOnSelect: true,
      },
    ],
    [schoolClassOptions, studentGroupOptions, t],
  );
  const deliveryTimeOptions = useMemo(
    () =>
      SCHOOL_LUNCH_BOX_ORDER_DELIVERY_TIMES.map((value) => ({
        label: t(getLunchBoxDeliveryTimeTranslationKey(value)),
        value,
      })),
    [t],
  );
  const lunchBoxById = useMemo(() => {
    const map = new Map<string, SchoolLunchBox>();
    for (const lunchBox of lunchBoxes) {
      map.set(lunchBox["@id"], lunchBox);
    }
    return map;
  }, [lunchBoxes]);

  const lunchBoxImagePrefetchPayload = useMemo(
    () => buildLunchBoxImagePrefetchPayload(lunchBoxes),
    [lunchBoxes],
  );

  useEffect(() => {
    if (typeof Image.prefetch !== "function") {
      return;
    }

    const primaryImageUrlsToPrefetch = JSON.parse(
      lunchBoxImagePrefetchPayload,
    ) as string[];

    if (primaryImageUrlsToPrefetch.length === 0) {
      return;
    }

    const prefetchLunchBoxImages = async () => {
      for (const primaryImageUrl of primaryImageUrlsToPrefetch) {
        if (processedPrefetchImageUrlsRef.current.has(primaryImageUrl)) {
          continue;
        }

        processedPrefetchImageUrlsRef.current.add(primaryImageUrl);

        try {
          await Image.prefetch(primaryImageUrl);
        } catch {
          // Ignore prefetch failures; the Image component will fall back on actual load errors.
        }
      }
    };

    void prefetchLunchBoxImages();
  }, [lunchBoxImagePrefetchPayload]);

  const NewLunchBoxesSchema = z
    .object({
      editor: z
        .string()
        .min(1, t("lunchBoxes.form.editorNameIsNeeded"))
        .refine((val) => !/\d/.test(val), {
          message: t("lunchBoxes.form.editorNameCannotContainNumbers"),
        }),
      date: z.string(t("lunchBoxes.form.dateIsNeeded")),
      time: z.enum(SCHOOL_LUNCH_BOX_ORDER_DELIVERY_TIMES, {
        error: t("lunchBoxes.form.timeIsNeeded"),
      }),
      classNameOrGroup: z
        .string(t("lunchBoxes.form.classNameIsNeeded"))
        .refine((value) => value !== CUSTOM_GROUP_OPTION_VALUE, {
          message: t("lunchBoxes.form.classNameOrGroupCustomMustBeCreated"),
        }),
      customGroupName: z.string().optional(),
      deliveryAddress: z.string(t("lunchBoxes.form.addressIsNeeded")),
      amount: z
        .string()
        .min(1, t("lunchBoxes.form.amountIsNeeded"))
        .refine((val) => /^[0-9]+$/.test(val), {
          message: t("lunchBoxes.form.amountMustBeNumeric"),
        }),
      peopleWithAllergies: z
        .array(z.string())
        .optional()
        .superRefine((peopleWithAllergies, ctx) => {
          const peopleWithAllergiesInputs =
            ensurePeopleWithAllergiesInputs(peopleWithAllergies);
          const hasAtLeastOnePersonWithAllergiesName =
            peopleWithAllergiesInputs.some(
              (personWithAllergiesName) =>
                personWithAllergiesName.trim().length > 0,
            );
          const shouldValidatePeopleWithAllergies =
            hasAtLeastOnePersonWithAllergiesName ||
            peopleWithAllergiesInputs.length > MIN_PEOPLE_WITH_ALLERGIES_FIELDS;

          if (!shouldValidatePeopleWithAllergies) {
            return;
          }

          peopleWithAllergiesInputs.forEach(
            (personWithAllergiesName, index) => {
              // Ignore validation for very first input if empty
              if (index === 0 && personWithAllergiesName.trim().length === 0) {
                return;
              }

              const normalizedPersonWithAllergiesName =
                personWithAllergiesName.trim();

              if (!normalizedPersonWithAllergiesName) {
                ctx.addIssue({
                  code: "custom",
                  path: [index],
                  message: t("lunchBoxes.form.peopleWithAllergiesNameIsNeeded"),
                });
                return;
              }

              if (
                normalizedPersonWithAllergiesName.length <=
                MAX_PERSON_WITH_ALLERGIES_NAME_LENGTH
              ) {
                return;
              }

              ctx.addIssue({
                code: "custom",
                path: [index],
                message: t("lunchBoxes.form.peopleWithAllergiesNameTooLong", {
                  maxLength: MAX_PERSON_WITH_ALLERGIES_NAME_LENGTH,
                }),
              });
            },
          );
        }),
      lunchBox: z.string(t("lunchBoxes.form.lunchBoxIsNeeded")),
      comment: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        /^[0-9]+$/.test(data.amount) &&
        sanitizePeopleWithAllergies(data.peopleWithAllergies).length >
          Number(data.amount)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["amount"],
          message: t("lunchBoxes.form.amountLessThanPeopleWithAllergies"),
        });
      }

      if (!data.date) {
        return;
      }

      const parsedDeliveryDate = parse(data.date, DATE_ONLY_FORMAT, new Date());

      if (isValid(parsedDeliveryDate) && isWeekend(parsedDeliveryDate)) {
        ctx.addIssue({
          code: "custom",
          path: ["date"],
          message: t("lunchBoxes.form.dateWeekendNotAllowed"),
        });
        return;
      }

      if (!data.lunchBox) {
        return;
      }

      const selectedLunchBox = lunchBoxById.get(data.lunchBox);
      if (!selectedLunchBox) {
        return;
      }

      const minBusinessDays = getMinBusinessDaysForLunchBox(
        selectedLunchBox.isLunaBox,
      );
      const currentOrderTime = getCurrentOrderTime();

      if (
        !hasEnoughBusinessDaysBeforeLunchBoxDelivery(
          data.date,
          currentOrderTime,
          minBusinessDays,
        )
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["date"],
          message: t("lunchBoxes.form.dateLeadTimeNotMet", {
            minBusinessDays,
          }),
        });
      }
    });

  const form = useFormWrapper<typeof NewLunchBoxesSchema>({
    schema: NewLunchBoxesSchema,
    defaultValues: {
      editor: "",
      date: undefined,
      time: undefined,
      classNameOrGroup: undefined,
      customGroupName: "",
      deliveryAddress: undefined,
      amount: "",
      peopleWithAllergies: [""],
      lunchBox: undefined,
      comment: "",
    },
    mode: "onSubmit",
    rethrowSubmitError: true,
  });
  const { getValues, setValue } = form;

  const selectedLunchBoxId = form.watch("lunchBox");
  const selectedDeliveryDate = form.watch("date");
  const selectedClassNameOrGroup = form.watch("classNameOrGroup");
  const amount = Number(form.watch("amount"));
  const peopleWithAllergiesInputs = ensurePeopleWithAllergiesInputs(
    form.watch("peopleWithAllergies"),
  );
  const isCustomStudentGroupSelected =
    selectedClassNameOrGroup === CUSTOM_GROUP_OPTION_VALUE;
  const customStudentGroupName = form.watch("customGroupName");

  useEffect(() => {
    if (selectedClassNameOrGroup !== CUSTOM_GROUP_OPTION_VALUE) {
      form.clearErrors("customGroupName");
    }
  }, [form, selectedClassNameOrGroup]);

  const isCreateStudentGroupDisabled =
    !isCustomStudentGroupSelected ||
    isStudentGroupRequestInFlight ||
    isRequestInFlight ||
    form.formState.isSubmitting ||
    !customStudentGroupName?.trim();
  const isAddPersonWithAllergiesFieldDisabled =
    !Number.isSafeInteger(amount) ||
    amount <= peopleWithAllergiesInputs.length ||
    isStudentGroupRequestInFlight ||
    isRequestInFlight ||
    form.formState.isSubmitting;
  const selectedLunchBox = selectedLunchBoxId
    ? lunchBoxById.get(selectedLunchBoxId)
    : undefined;
  const lunchBoxesAvailableForSelectedDate = selectedDeliveryDate
    ? lunchBoxes.filter((lunchBox) =>
        hasEnoughBusinessDaysBeforeLunchBoxDelivery(
          selectedDeliveryDate,
          orderTimeForView,
          getMinBusinessDaysForLunchBox(lunchBox.isLunaBox),
        ),
      )
    : lunchBoxes;

  const canSelectNonLunaLunchBox = selectedDeliveryDate
    ? hasEnoughBusinessDaysBeforeLunchBoxDelivery(
        selectedDeliveryDate,
        orderTimeForView,
        getMinBusinessDaysForLunchBox(false),
      )
    : true;
  const firstLunaLunchBoxId = useMemo(
    () => lunchBoxes.find((lunchBox) => lunchBox.isLunaBox)?.["@id"],
    [lunchBoxes],
  );
  const shouldPreselectLunaLunchBox = Boolean(
    selectedDeliveryDate && !canSelectNonLunaLunchBox && firstLunaLunchBoxId,
  );

  useEffect(() => {
    if (!shouldPreselectLunaLunchBox || !firstLunaLunchBoxId) {
      return;
    }

    if (getValues("lunchBox") === firstLunaLunchBoxId) {
      return;
    }

    setValue("lunchBox", firstLunaLunchBoxId, {
      shouldValidate: true,
    });
  }, [firstLunaLunchBoxId, getValues, setValue, shouldPreselectLunaLunchBox]);

  const minBusinessDaysForSelectedLunchBox = selectedLunchBox
    ? getMinBusinessDaysForLunchBox(selectedLunchBox.isLunaBox)
    : MIN_BUSINESS_DAYS_FOR_LUNA_BOX;
  const minDeliveryDate = getMinLunchBoxDeliveryDate(
    orderTimeForView,
    minBusinessDaysForSelectedLunchBox,
  );

  const onSubmit: Parameters<typeof form.submit>[0] = async ({ values }) => {
    if (submitInFlightRef.current) {
      return;
    }
    if (!schoolPath) {
      console.error("Cannot create lunch box order without a school");
      return;
    }
    submitInFlightRef.current = true;
    setIsRequestInFlight(true);

    const quantity = Number(values.amount);
    const comment = values.comment?.trim();
    const normalizedPeopleWithAllergies = sanitizePeopleWithAllergies(
      values.peopleWithAllergies,
    );
    const payload: SchoolLunchBoxOrderInput = {
      cafeteria: values.deliveryAddress,
      quantity,
      deliveryDate: values.date as SchoolLunchBoxOrderInput["deliveryDate"],
      deliveryTime: values.time,
      lunchBox: values.lunchBox,
      editedBy: values.editor,
    };

    if (
      values.classNameOrGroup !== ALL_CLASSES_OPTION_VALUE &&
      values.classNameOrGroup !== CUSTOM_GROUP_OPTION_VALUE
    ) {
      if (values.classNameOrGroup.startsWith(GROUP_OPTION_VALUE_PREFIX)) {
        payload.studentGroup = values.classNameOrGroup.slice(
          GROUP_OPTION_VALUE_PREFIX.length,
        );
      } else {
        payload.schoolClass = values.classNameOrGroup;
      }
    }

    if (comment) {
      payload.comment = comment;
    }

    if (normalizedPeopleWithAllergies.length > 0) {
      payload.peopleWithAllergies = normalizedPeopleWithAllergies;
    }

    try {
      await request<SchoolLunchBoxOrder>(`${schoolPath}/lunch-box-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/ld+json",
        },
        body: JSON.stringify(payload),
      });

      onSubmitSuccess?.(values as NewLunchBoxesFormValues);
    } catch (error) {
      console.error("Failed to create lunch box order:", error);
      onSubmitError?.(error);
      throw error;
    } finally {
      submitInFlightRef.current = false;
      setIsRequestInFlight(false);
    }
  };

  const onCreateStudentGroup = async () => {
    if (isStudentGroupRequestInFlight) {
      return;
    }
    if (!schoolPath) {
      console.error("Cannot create student group without a school");
      return;
    }

    const newStudentGroupName = form.getValues("customGroupName")?.trim() ?? "";
    if (!newStudentGroupName) {
      form.setError("customGroupName", {
        type: "manual",
        message: t("lunchBoxes.form.classNameOrGroupCustomNameIsNeeded"),
      });
      return;
    }

    setIsStudentGroupRequestInFlight(true);
    try {
      const createStudentGroupPayload: SchoolStudentGroupInput = {
        name: newStudentGroupName,
      };
      const createdStudentGroup = await request<SchoolStudentGroup>(
        `${schoolPath}/student-groups`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/ld+json",
          },
          body: JSON.stringify(createStudentGroupPayload),
        },
      );

      if (!createdStudentGroup?.["@id"]) {
        return;
      }
      try {
        await schoolStudentGroupsQuery.refetch();
      } catch (refreshError) {
        console.error(
          "Failed to refresh student groups after creation:",
          refreshError,
        );
        return;
      }
      form.clearErrors("customGroupName");
      form.setValue("customGroupName", "", {
        shouldDirty: true,
      });
      form.setValue(
        "classNameOrGroup",
        `${GROUP_OPTION_VALUE_PREFIX}${createdStudentGroup["@id"]}`,
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    } catch (error) {
      console.error("Failed to create student group:", error);
      const messageKey = hasDuplicateStudentGroupNameViolation(error)
        ? "lunchBoxes.form.classNameOrGroupCustomAlreadyExists"
        : "lunchBoxes.form.classNameOrGroupCustomCreateFailed";
      form.setError("customGroupName", {
        type: "manual",
        message: t(messageKey),
      });
    } finally {
      setIsStudentGroupRequestInFlight(false);
    }
  };

  const lunchBoxOptions = lunchBoxesAvailableForSelectedDate.map(
    (lunchBox) => ({
      label: lunchBox.title,
      value: lunchBox["@id"],
      renderingLabel: () => (
        <LunchBoxOptionLabel
          lunchBox={lunchBox}
          titleStyle={fonts.bold}
          descriptionStyle={fonts.semiBold}
        />
      ),
    }),
  );

  const onAddPersonWithAllergiesNameField = () => {
    const currentPeopleWithAllergies = ensurePeopleWithAllergiesInputs(
      form.getValues("peopleWithAllergies"),
    );
    const currentAmount = Number(form.getValues("amount"));

    if (
      isAddPersonWithAllergiesFieldDisabled ||
      !Number.isSafeInteger(currentAmount) ||
      currentPeopleWithAllergies.length >= currentAmount
    ) {
      return;
    }

    form.setValue("peopleWithAllergies", [...currentPeopleWithAllergies, ""], {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const onRemovePersonWithAllergiesNameField = (
    personWithAllergiesIndex: number,
  ) => {
    const currentPeopleWithAllergies = ensurePeopleWithAllergiesInputs(
      form.getValues("peopleWithAllergies"),
    );

    if (currentPeopleWithAllergies.length <= MIN_PEOPLE_WITH_ALLERGIES_FIELDS) {
      form.setValue("peopleWithAllergies", [""], {
        shouldDirty: true,
        shouldValidate: false,
      });
      form.clearErrors("peopleWithAllergies");
      return;
    }

    form.setValue(
      "peopleWithAllergies",
      currentPeopleWithAllergies.filter(
        (_, index) => index !== personWithAllergiesIndex,
      ),
      {
        shouldDirty: true,
        shouldValidate: false,
      },
    );
    form.clearErrors("peopleWithAllergies");
  };

  return (
    <FormProvider form={form}>
      <View style={styles.wrapper}>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.addressLabel")}*</ThemedText>
            <View style={styles.formContainer}>
              <View style={styles.formElement}>
                <FormSelect
                  name="classNameOrGroup"
                  options={classNameOrGroupOptions}
                  placeholder={t("lunchBoxes.form.classNamePlaceholder")}
                  clearable={false}
                  dropdownFooter={
                    isCustomStudentGroupSelected ? (
                      <View style={styles.customGroupContainer}>
                        <FormTextInput
                          name="customGroupName"
                          label={t(
                            "lunchBoxes.form.classNameOrGroupCustomNamePlaceholder",
                          )}
                          reserveErrorSpace={false}
                        />
                        <Button
                          buttonText={
                            isStudentGroupRequestInFlight
                              ? t("common.submitting")
                              : t(
                                  "lunchBoxes.form.classNameOrGroupCustomCreate",
                                )
                          }
                          onPress={() => {
                            void onCreateStudentGroup();
                          }}
                          disabled={isCreateStudentGroupDisabled}
                          buttonStyle={[
                            styles.customGroupButton,
                            { backgroundColor: colors.border },
                          ]}
                          buttonTextStyle={[
                            styles.buttonText,
                            { color: colors.background, ...fonts.bold },
                          ]}
                          iconName="plus"
                          iconPosition="left"
                          iconColor={colors.background}
                          iconSize={24}
                        />
                      </View>
                    ) : null
                  }
                />
              </View>
              <View style={styles.formElement}>
                <FormSelect
                  name="deliveryAddress"
                  options={cafeteriaOptions}
                  placeholder={t("lunchBoxes.form.addressPlaceholder")}
                  clearable={false}
                />
              </View>
            </View>
          </View>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.dateLabel")}*</ThemedText>
            <View style={styles.formContainer}>
              <View style={styles.formElement}>
                <FormDatePicker
                  name="date"
                  placeholder={t("lunchBoxes.form.datePlaceholder")}
                  minDate={minDeliveryDate}
                  isDateDisabled={isWeekend}
                />
              </View>
              <View style={styles.formElement}>
                <FormSelect
                  numColumns={1}
                  name="time"
                  options={deliveryTimeOptions}
                  placeholder={t("lunchBoxes.form.timePlaceholder")}
                  clearable={false}
                />
              </View>
            </View>
          </View>
        </View>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.amountLabel")}*</ThemedText>
            <FormTextInput
              name="amount"
              type="numeric"
              label={t("lunchBoxes.form.amountPlaceholder")}
            />
          </View>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.lunchBoxLabel")}*</ThemedText>
            <FormSelect
              icon={{
                iconSet: "font-awesome",
                icon: "crate-apple",
                style: "regular",
              }}
              name="lunchBox"
              options={lunchBoxOptions}
              clearable={false}
              placeholder={t("lunchBoxes.form.lunchBoxPlaceholder")}
              disabled={lunchBoxOptions.length === 0}
            />
          </View>
        </View>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText>
              {t("lunchBoxes.form.peopleWithAllergiesLabel")}
            </ThemedText>
            {peopleWithAllergiesInputs.map((_, index) => (
              <View
                key={`person-with-allergies-name-input-${index}`}
                style={
                  index > 0
                    ? [
                        styles.peopleWithAllergiesEntry,
                        styles.peopleWithAllergiesEntryWithRemove,
                      ]
                    : styles.peopleWithAllergiesEntry
                }
              >
                <View
                  style={index > 0 ? styles.peopleWithAllergiesNameField : null}
                >
                  <FormTextInput
                    name={`peopleWithAllergies.${index}`}
                    label={t(
                      "lunchBoxes.form.peopleWithAllergiesNamePlaceholder",
                    )}
                    description={
                      index === 0
                        ? t("lunchBoxes.form.peopleWithAllergiesDescription")
                        : undefined
                    }
                    reserveErrorSpace={false}
                  />
                </View>
                {index > 0 ? (
                  <Button
                    buttonText={t(
                      "lunchBoxes.form.peopleWithAllergiesNameRemoveButton",
                    )}
                    onPress={() => {
                      onRemovePersonWithAllergiesNameField(index);
                    }}
                    disabled={
                      isRequestInFlight ||
                      isStudentGroupRequestInFlight ||
                      form.formState.isSubmitting
                    }
                    buttonStyle={[
                      styles.resetButton,
                      styles.peopleWithAllergiesActionButton,
                      styles.peopleWithAllergiesRemoveButton,
                      { borderColor: colors.text },
                    ]}
                    buttonTextStyle={[
                      styles.buttonText,
                      { color: colors.text, ...fonts.bold },
                    ]}
                    iconName="delete-outline"
                    iconPosition="left"
                    iconColor={colors.text}
                    iconSize={32}
                  />
                ) : null}
              </View>
            ))}
            <Button
              buttonText={t("lunchBoxes.form.peopleWithAllergiesNameAddButton")}
              onPress={() => {
                onAddPersonWithAllergiesNameField();
              }}
              disabled={isAddPersonWithAllergiesFieldDisabled}
              buttonStyle={[
                styles.resetButton,
                styles.peopleWithAllergiesActionButton,
                styles.peopleWithAllergiesAddButton,
                { borderColor: colors.text },
              ]}
              buttonTextStyle={[
                styles.buttonText,
                { color: colors.text, ...fonts.bold },
              ]}
              iconName="plus"
              iconPosition="left"
              iconColor={colors.text}
              iconSize={32}
            />
          </View>
          <View style={styles.formElement} />
        </View>
        <View style={styles.formContainer}>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.editorLabel")}*</ThemedText>
            <FormTextInput
              icon={{
                iconSet: "material-community",
                icon: "account-circle-outline",
              }}
              name="editor"
              label={t("lunchBoxes.form.editorPlaceholder")}
            />
          </View>
          <View style={styles.formElement}>
            <ThemedText>{t("lunchBoxes.form.commentLabel")}</ThemedText>
            <FormTextInput
              multiline
              icon={{ iconSet: "material-community", icon: "playlist-edit" }}
              name="comment"
              label={t("lunchBoxes.form.commentPlaceholder")}
              description={t("lunchBoxes.form.commentDescription")}
            />
          </View>
        </View>
        <NoticeBox
          title={t("common.notice")}
          textContent={t("lunchBoxes.createLunchBoxNotice")}
        />
        <Text style={[styles.requiredFieldsHint, fonts.regular]}>
          {t("common.form.requiredFieldsHint")}
        </Text>
        <View style={styles.buttonContainer}>
          <ResetButton
            title={t("lunchBoxes.form.resetButton")}
            disabled={
              isRequestInFlight ||
              isStudentGroupRequestInFlight ||
              form.formState.isSubmitting
            }
            buttonStyle={[styles.resetButton, { borderColor: colors.text }]}
            buttonTextStyle={[
              styles.buttonText,
              { color: colors.text, ...fonts.bold },
            ]}
            iconName="delete-outline"
            iconPosition="left"
            iconColor={colors.text}
          />
          <SubmitButton
            title={t("lunchBoxes.form.createLunchBox")}
            disabled={
              isRequestInFlight ||
              isStudentGroupRequestInFlight ||
              form.formState.isSubmitting
            }
            onPress={() => {
              if (submitInFlightRef.current || form.formState.isSubmitting) {
                return;
              }
              void form
                .submit(onSubmit, () => {
                  onSubmitInvalid?.();
                })
                .catch(() => undefined);
            }}
            buttonStyle={[
              styles.submitButton,
              { backgroundColor: colors.border },
            ]}
            buttonTextStyle={[
              styles.buttonText,
              { color: colors.background, ...fonts.bold },
            ]}
            iconName="arrow-right"
            iconPosition="right"
            iconColor={colors.background}
          />
        </View>
      </View>
    </FormProvider>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    gap: 16,
  },
  submitButton: {
    borderRadius: 56,
    height: 56,
  },
  resetButton: {
    borderRadius: 56,
    borderWidth: 2,
    height: 56,
  },
  buttonText: {
    fontSize: 16,
  },
  formContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: 32,
  },
  formElement: {
    flex: 1,
    gap: 16,
  },
  peopleWithAllergiesEntry: {
    gap: 12,
  },
  peopleWithAllergiesEntryWithRemove: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  peopleWithAllergiesNameField: {
    flex: 1,
  },
  peopleWithAllergiesActionButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 16,
  },
  peopleWithAllergiesAddButton: {
    marginBottom: 30,
  },
  peopleWithAllergiesRemoveButton: {
    marginBottom: 0,
  },
  customGroupContainer: {
    gap: 12,
  },
  customGroupButton: {
    borderRadius: 56,
    height: 56,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  lunchBoxOption: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  lunchBoxOptionTextContainer: {
    flex: 1,
  },
  lunchBoxOptionImage: {
    width: 40,
    height: 40,
    marginRight: 8,
    borderRadius: 8,
  },
  lunchBoxOptionDescription: {
    fontSize: 12,
  },
  requiredFieldsHint: {
    fontSize: 16,
    lineHeight: 20.8,
    marginVertical: 16,
  },
});
